import { useMemo } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    FilterBarRange,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    Column,
    getPaginationRowModel,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";

import {
    SimpleTestingProgramListDto as TestingProgramListDto,
    SimpleTestingProgram,
    UserOverview,
} from "api/generated/model";
import { AccessRightTags } from "components/accessRightTags";
import { toUsersByAccessRights } from "components/accessRightTags/utils";
import { buildTestingProgramTestingControlsListUrl } from "org/controlTesting/urlUtils";
import { mapAsResourceWithoutAction } from "utils/dtoMapping";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";

import {
    AccessRightsFilterInput,
    AccessRightsFilterInputValue,
} from "../../../../components/table/AccessRightsFilterInput/AccessRightsFilterInput";
import { accessRightsFilterFunction } from "../../../../components/table/AccessRightsFilterInput/utils";

import {
    TESTING_PROGRAM_LIST_COLUMN_IDS as ID,
    TESTING_PROGRAM_TABLE_STATE_STORAGE_KEY,
    testingProgramListColumnFiltersCodecMap,
    testingProgramListTableDefaultValues,
} from "./consts";

type TestingProgramListColumnFilters = ZodCodecMapToType<
    typeof testingProgramListColumnFiltersCodecMap
>;

type TestingProgramColumnId = (typeof ID)[keyof typeof ID];

export function useTestingProgramListTable(data: TestingProgramListDto) {
    const { t } = useTranslation();
    const columns = buildColumns(t, data.linkedResources.users);

    const tableData = useMemo(
        () => mapAsResourceWithoutAction(data.content),
        [data]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");
    const stateAndOnChanges = usePresetTableSearchParams(
        TESTING_PROGRAM_TABLE_STATE_STORAGE_KEY,
        testingProgramListTableDefaultValues,
        testingProgramListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );

    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        initialState: {
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<SimpleTestingProgram>(),
        getSortedRowModel: getSortedRowModel<SimpleTestingProgram>(),
        getFilteredRowModel: getFilteredRowModel<SimpleTestingProgram>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<SimpleTestingProgram>(),
        getFacetedUniqueValues: getFacetedUniqueValues<SimpleTestingProgram>(),
        getPaginationRowModel: getPaginationRowModel(),
    });
    const filterComponent = (column: Column<SimpleTestingProgram>) =>
        getFilterComponent(t, column, data);

    return {
        table,
        getFilterComponent: filterComponent,
        tableId: TESTING_PROGRAM_TABLE_STATE_STORAGE_KEY,
    };
}

function TestingProgramNameLink({
    testingProgramId,
    name,
}: {
    testingProgramId: number;
    name: string;
}) {
    return (
        <Link to={buildTestingProgramTestingControlsListUrl(testingProgramId)}>
            {name}
        </Link>
    );
}

const columnHelper = createColumnHelper<SimpleTestingProgram>();
function buildColumns(t: TFunction, users: UserOverview[]) {
    return [
        columnHelper.accessor((row) => row.name, {
            id: ID.NAME,
            cell: (info) => (
                <TestingProgramNameLink
                    testingProgramId={info.row.original.id}
                    name={info.getValue()}
                />
            ),
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: false,
            header: () => t("TestingProgramList_NameColumn"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.testingControlCount, {
            id: ID.TESTING_CONTROL_COUNT,
            enableSorting: true,
            enableColumnFilter: true,
            sortingFn: "alphanumeric",
            filterFn: createFilterFnWithMode<
                SimpleTestingProgram,
                [number | null, number | null]
            >((row, filterValue) => {
                const value = row.getValue(ID.TESTING_CONTROL_COUNT);
                if (typeof value !== "number") {
                    return false;
                }
                const [min, max] = filterValue;
                const minCheck = min === null || value >= min;
                const maxCheck = max === null || value <= max;
                return minCheck && maxCheck;
            }),
            cell: (info) => info.getValue(),
            header: () => t("TestingProgramList_TestingControlCountColumn"),
            footer: (props) => props.column.id,
            size: TST_COLUMN_SIZE.S,
        }),
        columnHelper.accessor((row) => row.accessRights, {
            id: ID.ACCESS_RIGHTS,
            enableColumnFilter: true,
            enableGlobalFilter: false,
            header: () => t("TestingProgramList_AccessRightsColumn"),
            size: TST_COLUMN_SIZE.L,
            sortingFn: Cmp.map(
                (row) =>
                    Object.values(row.original.accessRights).reduce(
                        (acc, value) => acc + value.length,
                        0
                    ),
                Cmp.primitive
            ),
            filterFn: createFilterFnWithMode<
                SimpleTestingProgram,
                AccessRightsFilterInputValue
            >((row, value) =>
                accessRightsFilterFunction(row.original.accessRights, value)
            ),
            cell: (info) => {
                return (
                    <AccessRightTags
                        usersByAccessRight={toUsersByAccessRights(
                            info.getValue(),
                            users
                        )}
                    />
                );
            },
        }),
    ];
}
function getFilterComponent(
    t: TFunction,
    column: Column<SimpleTestingProgram>,
    sourceData: TestingProgramListDto
) {
    const getFilterHelpers =
        createFilterHelpersGetter<TestingProgramListColumnFilters>(column);
    const columnId = column.id as TestingProgramColumnId;

    switch (columnId) {
        case ID.ACCESS_RIGHTS: {
            const { setFilterValue, value } = getFilterHelpers(columnId);
            return (
                <AccessRightsFilterInput
                    users={sourceData.linkedResources.users}
                    facetedValues={column.getFacetedUniqueValues()}
                    value={value as AccessRightsFilterInputValue | null}
                    onChange={(value) => {
                        setFilterValue(value as AccessRightsFilterInputValue);
                    }}
                />
            );
        }
        case ID.TESTING_CONTROL_COUNT: {
            const [min, max] = column.getFacetedMinMaxValues()!;
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <FilterBarRange
                    min={min}
                    max={max}
                    maxPlaceholderLabel={t("FilterNumRange_MaxPlaceholder")}
                    minPlaceholderLabel={t("FilterNumRange_MinPlaceholder")}
                    value={value as [number, number]}
                    onChange={(updater) => {
                        const prev = value ?? [null, null];
                        setFilterValue(updater(prev));
                    }}
                />
            );
        }
        case ID.NAME:
            throw new Error("Name should be marked as not filterable");
        default:
            return assertNever(columnId);
    }
}
